import {
  Box,
  Button,
  Input,
  NativeSelect,
  Portal,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Field, useEscapeToClosePopup } from '@mangovoice/mango-ui'

import {
  useLocationsSearch,
  usePbxCallerIdOptions,
} from '../hooks/useExtensionReferenceData'
import { useExtensionsI18n } from '../i18n/provider'
import type { CallerIdOption, LocationOption } from '../types/api.types'
import {
  LOCAL_PRESENCE_OPTIONS,
  PREFERRED_CODEC_OPTIONS,
} from '../utils/extensions'
import { SideSheetDialog } from './SideSheetDialog'

const NO_CHANGE = '__no_change__'
const DEBOUNCE_MS = 300
const DROPDOWN_MAX_HEIGHT = '240px'
const toI18nKey = (value: string | number) =>
  String(value).replace(/[^\w]/g, '_').toLowerCase()

export interface MassEditLinesFormPayload {
  outbound_external_callerid?: string | null
  use_local_presence?: number | null
  preferred_codec?: string | null
  location_id?: number | null
  pbx_location_id?: number | null
}

interface MassEditLinesDialogProps {
  isOpen: boolean
  onClose: () => void
  selectedCount: number
  pbxId: number | undefined | null
  onConfirm: (payload: MassEditLinesFormPayload) => void | Promise<void>
  isSubmitting: boolean
}

export const MassEditLinesDialog = React.memo<MassEditLinesDialogProps>(
  ({ isOpen, onClose, selectedCount, pbxId, onConfirm, isSubmitting }) => {
    const { text } = useExtensionsI18n()
    const [outboundCallerIdValue, setOutboundCallerIdValue] =
      useState(NO_CHANGE)
    const [localPresenceValue, setLocalPresenceValue] = useState(NO_CHANGE)
    const [preferredCodecValue, setPreferredCodecValue] = useState(NO_CHANGE)
    const [searchTerm, setSearchTerm] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [selectedLocation, setSelectedLocation] =
      useState<LocationOption | null>(null)
    const [dropdownOpen, setDropdownOpen] = useState(false)
    useEscapeToClosePopup(dropdownOpen, () => setDropdownOpen(false))
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
      const timer = setTimeout(
        () => setDebouncedSearch(searchTerm),
        DEBOUNCE_MS,
      )
      return () => clearTimeout(timer)
    }, [searchTerm])

    const { locations, isLoading } = useLocationsSearch(
      pbxId ?? undefined,
      debouncedSearch,
    )
    const { callerIdOptions } = usePbxCallerIdOptions(pbxId ?? undefined)

    useEffect(() => {
      if (!isOpen) {
        setOutboundCallerIdValue(NO_CHANGE)
        setLocalPresenceValue(NO_CHANGE)
        setPreferredCodecValue(NO_CHANGE)
        setSearchTerm('')
        setDebouncedSearch('')
        setSelectedLocation(null)
        setDropdownOpen(false)
      }
    }, [isOpen])

    const hasAnyChange = useMemo(
      () =>
        outboundCallerIdValue !== NO_CHANGE ||
        localPresenceValue !== NO_CHANGE ||
        preferredCodecValue !== NO_CHANGE ||
        selectedLocation != null,
      [
        outboundCallerIdValue,
        localPresenceValue,
        preferredCodecValue,
        selectedLocation,
      ],
    )

    const buildPayload = useCallback((): MassEditLinesFormPayload => {
      const payload: MassEditLinesFormPayload = {}
      if (outboundCallerIdValue !== NO_CHANGE) {
        payload.outbound_external_callerid =
          outboundCallerIdValue.trim() === ''
            ? null
            : outboundCallerIdValue.trim()
      }
      if (localPresenceValue !== NO_CHANGE) {
        payload.use_local_presence = parseInt(localPresenceValue, 10)
      }
      if (preferredCodecValue !== NO_CHANGE) {
        payload.preferred_codec =
          preferredCodecValue === '' ? null : preferredCodecValue
      }
      if (selectedLocation != null) {
        payload.location_id = selectedLocation.id
        payload.pbx_location_id = selectedLocation.id
      }
      return payload
    }, [
      outboundCallerIdValue,
      localPresenceValue,
      preferredCodecValue,
      selectedLocation,
    ])

    const handleSubmit = useCallback(() => {
      if (!hasAnyChange) return
      onConfirm(buildPayload())
      setOutboundCallerIdValue(NO_CHANGE)
      setLocalPresenceValue(NO_CHANGE)
      setPreferredCodecValue(NO_CHANGE)
      setSelectedLocation(null)
      setSearchTerm('')
    }, [hasAnyChange, buildPayload, onConfirm])

    const handleClose = useCallback(() => {
      if (!isSubmitting) {
        setOutboundCallerIdValue(NO_CHANGE)
        setLocalPresenceValue(NO_CHANGE)
        setPreferredCodecValue(NO_CHANGE)
        setSelectedLocation(null)
        setSearchTerm('')
        setDropdownOpen(false)
        onClose()
      }
    }, [isSubmitting, onClose])

    const handleSelectLocation = useCallback((loc: LocationOption) => {
      setSelectedLocation(loc)
      setSearchTerm('')
      setDropdownOpen(false)
    }, [])

    const canSubmit = hasAnyChange && !isSubmitting

    const inputDisplayValue = useMemo(() => {
      if (dropdownOpen) return searchTerm
      return selectedLocation?.name ?? ''
    }, [dropdownOpen, searchTerm, selectedLocation])

    const emptyStateText = useMemo(() => {
      if (isLoading)
        return text(
          'extensions.massEdit.loadingLocations',
          'Loading locations\u2026',
        )
      if (searchTerm.trim())
        return text(
          'extensions.massEdit.noLocationsFound',
          'No locations found',
        )
      return text(
        'extensions.massEdit.typeToSearchLocations',
        'Type to search locations',
      )
    }, [isLoading, searchTerm, text])

    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({})
    useEffect(() => {
      if (dropdownOpen && inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect()
        setDropdownStyle({
          position: 'fixed',
          top: `${rect.bottom + 4}px`,
          left: `${rect.left}px`,
          width: `${Math.max(rect.width, 280)}px`,
        })
      }
    }, [dropdownOpen])

    return (
      <SideSheetDialog
        isOpen={isOpen}
        onClose={handleClose}
        title={text('extensions.massEdit.title', 'Mass edit lines')}
        body={
          <>
            <Text mb={4}>
              {text(
                'extensions.massEdit.description',
                'Update the following fields for {{count}} line{{suffix}}. Leave as "No change" to keep current value.',
                {
                  count: selectedCount,
                  suffix: selectedCount !== 1 ? 's' : '',
                },
              )}
            </Text>
            <VStack gap={4} align="stretch">
              <Field.Root>
                <Field.Label>
                  {text(
                    'extensions.massEdit.outboundCallerId',
                    'Outbound caller ID',
                  )}
                </Field.Label>
                <NativeSelect.Root disabled={isSubmitting}>
                  <NativeSelect.Field
                    value={outboundCallerIdValue}
                    onChange={(e) => setOutboundCallerIdValue(e.target.value)}
                    px={3}
                  >
                    <option value={NO_CHANGE}>
                      {text('extensions.common.noChange', 'No change')}
                    </option>
                    <option value="">
                      {text('extensions.common.none', 'None')}
                    </option>
                    {callerIdOptions.map((opt: CallerIdOption) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>

              <Field.Root>
                <Field.Label>
                  {text('extensions.massEdit.localPresence', 'Local presence')}
                </Field.Label>
                <NativeSelect.Root disabled={isSubmitting}>
                  <NativeSelect.Field
                    value={localPresenceValue}
                    onChange={(e) => setLocalPresenceValue(e.target.value)}
                    px={3}
                  >
                    <option value={NO_CHANGE}>
                      {text('extensions.common.noChange', 'No change')}
                    </option>
                    {LOCAL_PRESENCE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {text(
                          `extensions.localPresence.${toI18nKey(opt.value)}`,
                          opt.label,
                        )}
                      </option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>

              <Field.Root>
                <Field.Label>
                  {text(
                    'extensions.massEdit.preferredCodec',
                    'Preferred codec',
                  )}
                </Field.Label>
                <NativeSelect.Root disabled={isSubmitting}>
                  <NativeSelect.Field
                    value={preferredCodecValue}
                    onChange={(e) => setPreferredCodecValue(e.target.value)}
                    px={3}
                  >
                    <option value={NO_CHANGE}>
                      {text('extensions.common.noChange', 'No change')}
                    </option>
                    {PREFERRED_CODEC_OPTIONS.map((opt) => (
                      <option key={opt.value || 'default'} value={opt.value}>
                        {text(
                          `extensions.codecOptions.${toI18nKey(opt.value || 'default')}`,
                          opt.label,
                        )}
                      </option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>

              <Box position="relative">
                <Field.Root>
                  <Field.Label>
                    {text('extensions.massEdit.location', 'Location')}
                  </Field.Label>
                  <Input
                    ref={inputRef}
                    value={inputDisplayValue}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setSearchTerm(e.target.value)
                      setDropdownOpen(true)
                      if (selectedLocation) setSelectedLocation(null)
                    }}
                    onFocus={() => setDropdownOpen(true)}
                    onBlur={() => setTimeout(() => setDropdownOpen(false), 200)}
                    placeholder={text(
                      'extensions.massEdit.locationPlaceholder',
                      'No change or type to search',
                    )}
                    disabled={isSubmitting || pbxId == null}
                    bg="bg.subtle"
                    borderColor="border.default"
                    aria-label={text(
                      'extensions.massEdit.locationSearchLabel',
                      'Location search',
                    )}
                    aria-autocomplete="list"
                    aria-expanded={dropdownOpen}
                  />
                </Field.Root>
                {isLoading && (
                  <Box
                    position="absolute"
                    right={3}
                    top="50%"
                    transform="translateY(-50%)"
                    mt={1}
                    pointerEvents="none"
                  >
                    <Spinner size="sm" />
                  </Box>
                )}
                {dropdownOpen && pbxId != null && (
                  <Portal>
                    <Box
                      style={dropdownStyle}
                      zIndex={1500}
                      bg="bg.panel"
                      borderWidth="1px"
                      borderColor="border.default"
                      borderRadius="md"
                      shadow="lg"
                      maxHeight={DROPDOWN_MAX_HEIGHT}
                      overflowY="auto"
                    >
                      {locations.length === 0 ? (
                        <Box p={4} textAlign="center" color="fg.muted">
                          <Text fontSize="sm">{emptyStateText}</Text>
                        </Box>
                      ) : (
                        <VStack gap={0} align="stretch" py={1}>
                          {locations.map((loc) => (
                            <Box
                              key={loc.id}
                              px={4}
                              py={2}
                              cursor="pointer"
                              bg={
                                selectedLocation?.id === loc.id
                                  ? 'blue.50'
                                  : 'transparent'
                              }
                              _hover={{ bg: 'gray.50' }}
                              onClick={() => handleSelectLocation(loc)}
                              onMouseDown={(e) => e.preventDefault()}
                            >
                              <Text fontWeight="medium">{loc.name}</Text>
                            </Box>
                          ))}
                        </VStack>
                      )}
                    </Box>
                  </Portal>
                )}
              </Box>
            </VStack>
          </>
        }
        footer={
          <Box display="flex" gap={3} justifyContent="flex-end">
            <Button
              type="button"
              variant="outline"
              colorPalette="neutral"
              onClick={handleClose}
              px={5}
              py={2.5}
              borderColor="border.default"
              _hover={{
                borderColor: 'border.emphasized',
                bg: 'bg.subtle',
              }}
              disabled={isSubmitting}
            >
              {text('extensions.common.cancel', 'Cancel')}
            </Button>
            <Button
              type="button"
              colorPalette="brand"
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={!canSubmit}
              px={5}
              py={2.5}
            >
              {text('extensions.common.apply', 'Apply')}
            </Button>
          </Box>
        }
      />
    )
  },
)

MassEditLinesDialog.displayName = 'MassEditLinesDialog'
